import React, { useEffect, useState } from 'react';
import {
    Modal,
    Button,
    Space,
    message,
    Typography,
    Alert,
    List,
    Tag,
    Spin,
    Card
} from 'antd';
import { GlobalOutlined, WarningOutlined, ArrowRightOutlined } from '@ant-design/icons';
import { roleService } from '../services/api';

const { Text, Title } = Typography;

const PRIMARY_RED = '#DA291C';

const PortalChangeModal = ({ visible, onClose, roleUuid, currentPortal, onSuccess }) => {
    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(false);
    const [impact, setImpact] = useState(null);

    const targetPortal = currentPortal === 'admin' ? 'agent' : 'admin';

    useEffect(() => {
        if (visible && roleUuid) {
            fetchImpact();
        }
    }, [visible, roleUuid]);

    const fetchImpact = async () => {
        setFetching(true);
        try {
            const res = await roleService.previewPortalChange(roleUuid);
            if (res.status === 'success') {
                setImpact(res.data);
            }
        } catch (error) {
            message.error('Error al obtener previsualización del cambio');
        } finally {
            setFetching(false);
        }
    };

    const handleConfirm = async () => {
        setLoading(true);
        try {
            const res = await roleService.changePortal(roleUuid, targetPortal);
            if (res.status === 'success') {
                message.success('Portal cambiado exitosamente');
                onSuccess();
                onClose();
            }
        } catch (error) {
            message.error(error.response?.data?.message || 'Error al cambiar portal');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal
            title={
                <Space>
                    <GlobalOutlined style={{ color: '#faad14' }} />
                    <span>Cambiar Portal de Rol</span>
                </Space>
            }
            open={visible}
            onCancel={onClose}
            width={550}
            footer={[
                <Button key="back" onClick={onClose}>
                    Cancelar
                </Button>,
                <Button
                    key="submit"
                    type="primary"
                    danger={targetPortal === 'admin'}
                    loading={loading}
                    onClick={handleConfirm}
                    style={{ background: targetPortal === 'admin' ? '#DA291C' : '#1890ff', borderColor: targetPortal === 'admin' ? '#DA291C' : '#1890ff' }}
                >
                    Confirmar Cambio a {targetPortal.toUpperCase()}
                </Button>
            ]}
        >
            <div style={{ marginBottom: 20 }}>
                <Text>Estás a punto de mover el rol <Text strong>"{impact?.role_name}"</Text> al portal <Tag color={targetPortal === 'admin' ? 'volcano' : 'blue'}>{targetPortal.toUpperCase()}</Tag>.</Text>
            </div>

            {fetching ? (
                <div style={{ textAlign: 'center', padding: '20px 0' }}>
                    <Spin tip="Calculando impacto..." />
                </div>
            ) : impact ? (
                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                    <Alert
                        message="Advertencia de Jerarquía"
                        description="Cambiar el portal de un rol romperá sus vínculos jerárquicos actuales para mantener la integridad de los datos."
                        type="warning"
                        showIcon
                        icon={<WarningOutlined />}
                    />

                    {impact.disconnected_parent && (
                        <Card size="small" title="Desvinculación de Padre" headStyle={{ background: '#fff7e6' }}>
                            <Space>
                                <Tag color="default">{impact.disconnected_parent}</Tag>
                                <ArrowRightOutlined />
                                <Text type="danger">Vínculo roto</Text>
                            </Space>
                            <div style={{ marginTop: 8 }}>
                                <Text type="secondary" style={{ fontSize: '12px' }}>
                                    El rol ya no heredará permisos de su padre actual.
                                </Text>
                            </div>
                        </Card>
                    )}

                    {impact.disconnected_children && impact.disconnected_children.length > 0 && (
                        <Card size="small" title={`Desvinculación de Hijos (${impact.disconnected_children.length})`} headStyle={{ background: '#fff7e6' }}>
                            <List
                                size="small"
                                dataSource={impact.disconnected_children}
                                renderItem={item => (
                                    <List.Item>
                                        <Text>{item.name}</Text>
                                        <Text type="secondary" style={{ fontSize: '12px' }}> (Se volverá independiente)</Text>
                                    </List.Item>
                                )}
                            />
                            <div style={{ marginTop: 8 }}>
                                <Text type="secondary" style={{ fontSize: '12px' }}>
                                    Estos roles ya no transmitirán sus permisos a "{impact.role_name}".
                                </Text>
                            </div>
                        </Card>
                    )}

                    {(!impact.disconnected_parent && (!impact.disconnected_children || impact.disconnected_children.length === 0)) && (
                        <Alert
                            message="Sin impacto jerárquico"
                            description="Este rol no tiene padres ni hijos vinculados, por lo que el cambio es seguro."
                            type="success"
                            showIcon
                        />
                    )}
                </Space>
            ) : null}
        </Modal>
    );
};

export default PortalChangeModal;
